import { InfoOutlined } from '@mui/icons-material';
import { ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { FieldProps } from '@rjsf/utils';
import { useMemo } from 'react';

type FieldSettings = {
  name: string;
  title?: string;
  icon?: JSX.Element;
  disabled?: boolean;
  description?: string;
};

type MultiBooleanToggleFieldSettings = {
  fields: FieldSettings[];
};

const MultiBooleanToggleField = (props: FieldProps) => {
  const { formData, onChange, disabled, readonly, uiSchema, schema } = props;
  const settings = (uiSchema?.['ui:options'] as MultiBooleanToggleFieldSettings) || { fields: [] };

  const selectedValues = useMemo(
    () => settings.fields.map((f) => f.name).filter((field) => formData?.[field]),
    [formData, settings.fields],
  );

  const handleChange = (_: React.MouseEvent, newValues: string[]) => {
    const newFormData = {
      ...formData,
      ...settings.fields.reduce(
        (acc, field) => {
          acc[field.name] = newValues.includes(field.name);
          return acc;
        },
        {} as Record<string, boolean>,
      ),
    };

    onChange(newFormData);
  };

  return (
    <>
      {schema.title && <Typography className="mb-1">{schema.title}</Typography>}
      <ToggleButtonGroup
        value={selectedValues}
        onChange={handleChange}
        aria-label="multi boolean toggle"
        color="primary"
        disabled={disabled || readonly}
      >
        {settings.fields.map((field) => (
          <ToggleButton key={field.name} value={field.name} disabled={field.disabled} className="flex gap-1">
            {field.icon}
            {field.title || field.name}
            {field.description && (
              <Tooltip title={field.description}>
                <InfoOutlined fontSize="inherit" />
              </Tooltip>
            )}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </>
  );
};

export default MultiBooleanToggleField;
